// ============================================
// Cloud Data Hooks - Replace useLiveQuery
// Auto-refetch when data changes via subscribe()
// Includes in-memory cache and in-flight deduplication
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { subscribe } from './cloud';
import { isNetworkFailureError } from './cloud/_shared';
import type { Table } from './cloud';

export type { Table };

// ---- Global cache: persists across navigations within a session ----
const cache = new Map<string, unknown>();

// ---- In-flight deduplication: prevents duplicate concurrent fetches ----
const inFlight = new Map<string, Promise<unknown>>();

// ---- Pending-invalidation set: tracks keys that were notify()'d while
// already in-flight, so we can fire a trailing refetch once the in-flight
// resolves. Without this, a second mutation that lands during a fetch
// joins the in-flight result (which is pre-mutation-2) and the UI never
// sees the post-mutation-2 state. Source: docs/launch-readiness-audit.md,
// julik-frontend-races-reviewer 2026-04-27.
const pendingInvalidation = new Set<string>();

// ---- Network-failure telemetry ----
// Fetch failures used to die in the catch below: shown on screen, never
// recorded. So when a user reported "couldn't load your protocols"
// (2026-09-08) there was no way to tell a Supabase/edge incident from one
// device's connectivity. These reports answer that: several users across
// carriers at once = infrastructure, one device = that phone.
//
// Two deliberate limits:
//  - Skip when the browser knows it is offline. An airplane-mode user is
//    not a signal; the case worth catching is "online, request still died".
//  - One report per query key per window, so a dead connection with a
//    dozen mounted hooks doesn't flood the project.
const NETWORK_REPORT_WINDOW_MS = 5 * 60 * 1000;
const lastNetworkReport = new Map<string, number>();

function reportNetworkFailure(key: string, err: unknown) {
  if (!isNetworkFailureError(err)) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

  const now = Date.now();
  const last = lastNetworkReport.get(key);
  if (last !== undefined && now - last < NETWORK_REPORT_WINDOW_MS) return;
  lastNetworkReport.set(key, now);

  // Sentry's beforeSend scrubber (src/main.tsx, PHI hardening P2-06) drops
  // tags, extra, and contexts, so the diagnostic context has to ride on the
  // exception itself. Both parts are safe: `key` is "table:fetcherName" and
  // technicalDetail is our own copy plus the browser's network message.
  const event = new Error(`${key} — ${err.technicalDetail}`, { cause: err });
  event.name = 'NetworkFailure';
  Sentry.captureException(event);
}

export function clearCache() {
  cache.clear();
  inFlight.clear();
  pendingInvalidation.clear();
  lastNetworkReport.clear();
}

/**
 * Alias for clearCache() intended for auth-layer callers (AuthProvider).
 * Clearing on SIGNED_OUT and user-id changes prevents User B from briefly
 * seeing User A's cached query results right after a sign-in.
 */
export function clearCloudQueryCache() {
  clearCache();
}

function getCacheKey(table: Table | Table[], fetcher: Function): string {
  const tables = Array.isArray(table) ? table.join('+') : table;
  const name = fetcher.name || String(fetcher).slice(0, 64);
  return `${tables}:${name}`;
}

/**
 * Generic hook that fetches data and auto-refetches when the table is invalidated.
 * Uses an in-memory cache so returning to a page shows data immediately.
 * Deduplicates in-flight requests so multiple components subscribing to the
 * same table+fetcher only trigger one network request.
 */
export function useCloudQuery<T>(
  table: Table | Table[],
  fetcher: () => Promise<T>,
  defaultValue: T,
): { data: T; loading: boolean; error: string | null; refetch: () => void } {
  const cacheKey = getCacheKey(table, fetcher);
  const cached = cache.get(cacheKey) as T | undefined;

  const [data, setData] = useState<T>(cached !== undefined ? cached : defaultValue);
  const [loading, setLoading] = useState(cached === undefined);
  const [error, setError] = useState<string | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;
  const mountedRef = useRef(true);

  const doFetch = useCallback(async () => {
    const key = cacheKeyRef.current;

    // If a fetch for this key is already in progress, mark that an
    // additional invalidation came in and reuse the in-flight result.
    // The trailing refetch (below) will fire after the in-flight
    // resolves so we always settle on current server state.
    if (inFlight.has(key)) {
      pendingInvalidation.add(key);
      try {
        const result = await inFlight.get(key) as T;
        if (mountedRef.current) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(err instanceof Error ? err.message : 'Failed to fetch data');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
      return;
    }

    // Start a fresh fetch. Clear any stale pending flag for this key
    // so we only refetch on invalidations that arrive *during* this
    // fetch, not before it.
    pendingInvalidation.delete(key);

    // No cached value means the last attempt never succeeded (or this is
    // the first) - re-enter loading so a Retry visibly retries (skeleton)
    // instead of leaving a frozen error card (2026-09-08 field report:
    // "Retry doesn't work" - it worked, silently). Background refetches
    // after a successful load have a warm cache and skip this, so no
    // app-wide skeleton flashes on mutations.
    if (cache.get(key) === undefined && mountedRef.current) {
      setLoading(true);
    }

    const promise = fetcherRef.current();
    inFlight.set(key, promise);

    try {
      const result = await promise;
      cache.set(key, result);
      if (mountedRef.current) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      // Report before the mounted guard: the failure happened whether or
      // not this component is still on screen.
      reportNetworkFailure(key, err);
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      }
    } finally {
      inFlight.delete(key);
      if (mountedRef.current) setLoading(false);
    }

    // Trailing refetch: if any invalidation arrived during the fetch,
    // run another pass so the UI converges on latest server state.
    if (pendingInvalidation.has(key)) {
      pendingInvalidation.delete(key);
      doFetch();
    }
  }, []);

  // Initial fetch + subscribe to invalidation
  useEffect(() => {
    mountedRef.current = true;
    doFetch();
    const tables = Array.isArray(table) ? table : [table];
    const unsubs = tables.map((t) => subscribe(t, doFetch));
    return () => {
      mountedRef.current = false;
      unsubs.forEach((u) => u());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error, refetch: doFetch };
}
